/**
 * JsKernel — the fast in-JS geometry kernel behind the timeline evaluator.
 *
 * Covers the interactive core: sketch profile extraction and extrude
 * (push/pull). Booleans (cut/intersect), revolve/sweep/shell/fillet/chamfer
 * and STEP-grade topology arrive with the OpenCascade.js worker kernel in
 * Step 6, behind this same GeomKernel interface.
 */

import { applyBoolean } from './booleans';
import { extrudeProfiles } from './extrude';
import { revolveProfiles } from './revolve';
import {
  buildSketchShapes,
  offsetPlaneMatrix,
  planeBasis,
  planeToWorldMatrix,
  uvToWorld,
} from './sketch';
import { sweepProfiles } from './sweep';
import { expandTextEntities } from '../text/fontCache';
import { bakeTransform } from '../io/mesh';
import { loadParsedMesh } from '../storage/assets';
import {
  FeatureError,
  type EvalContext,
  type GeometryResult,
  type GeomKernel,
  type MeshData,
  type ProfileRegion,
} from './types';
import type {
  ExtrudeParams,
  FeatureId,
  FeatureNode,
  GroupParams,
  ImportMeshParams,
  InstanceParams,
  MirrorParams,
  RevolveParams,
  ScaleParams,
  SketchParams,
  SweepParams,
  TransformParams,
} from '../timeline/types';

export class JsKernel implements GeomKernel {
  async execute(feature: FeatureNode, ctx: EvalContext): Promise<GeometryResult> {
    switch (feature.type) {
      case 'sketch':
        return this.sketch(feature as FeatureNode<'sketch'>, ctx);
      case 'extrude':
        return this.extrude(feature as FeatureNode<'extrude'>, ctx);
      case 'revolve':
        return this.revolve(feature as FeatureNode<'revolve'>, ctx);
      case 'sweep':
        return this.sweep(feature as FeatureNode<'sweep'>, ctx);
      case 'scale':
        return this.scale(feature as FeatureNode<'scale'>, ctx);
      case 'mirror':
        return this.mirror(feature as FeatureNode<'mirror'>, ctx);
      case 'transform':
        return this.transform(feature as FeatureNode<'transform'>, ctx);
      case 'group':
        return this.group(feature as FeatureNode<'group'>, ctx);
      case 'instance':
        return this.instance(feature as FeatureNode<'instance'>, ctx);
      case 'part':
        return this.part(feature as FeatureNode<'part'>, ctx);
      case 'import-mesh':
        return this.importMesh(feature as FeatureNode<'import-mesh'>);
      default:
        throw new FeatureError(
          feature.id,
          `${feature.type} needs the B-rep kernel (planned for the OpenCascade.js worker)`,
        );
    }
  }

  private sketch(feature: FeatureNode<'sketch'>, ctx: EvalContext): GeometryResult {
    const params = feature.params as SketchParams;
    try {
      const matrix = this.resolvePlaneMatrix(params.plane, ctx);
      // Text → glyph outline loops (main thread owns the font).
      const entities = expandTextEntities(params.entities);
      const { profiles, paths } = buildSketchShapes(entities, matrix);
      return {
        kind: 'sketch',
        profiles,
        paths,
        planeWorld: matrix,
        entities,
      };
    } catch (err) {
      throw new FeatureError(feature.id, err instanceof Error ? err.message : String(err));
    }
  }

  /**
   * Datum planes resolve statically; face planes resolve from the referenced
   * body's stable-named cap ('face:top'/'face:bottom') — so the sketch RIDES
   * that face: thicken the plate and everything on top re-locates.
   */
  private resolvePlaneMatrix(plane: SketchParams['plane'], ctx: EvalContext): Float32Array {
    if (plane.kind === 'bface') {
      // Parametric B-rep face: nearest planar face (by pick-time centroid)
      // of the body's CURRENT geometry. Rides the face through edits.
      const input = ctx.inputs.get(plane.body.$feature);
      if (input?.kind !== 'body' || !input.mesh.faceKinds || !input.mesh.faceData) {
        throw new Error('Sketch face reference needs an exact B-rep body');
      }
      const kinds = input.mesh.faceKinds;
      const d = input.mesh.faceData;
      let best = -1;
      let bestDist = Infinity;
      for (let i = 0; i < kinds.length; i++) {
        if (kinds[i] !== 1) continue;
        const dist = Math.hypot(
          d[i * 10]! - plane.center[0],
          d[i * 10 + 1]! - plane.center[1],
          d[i * 10 + 2]! - plane.center[2],
        );
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      }
      if (best < 0) throw new Error('Body has no planar face for this sketch');
      return planeToWorldMatrix(
        planeBasis({
          kind: 'free',
          origin: [d[best * 10]!, d[best * 10 + 1]!, d[best * 10 + 2]!],
          normal: [d[best * 10 + 3]!, d[best * 10 + 4]!, d[best * 10 + 5]!],
        }),
      );
    }
    if (plane.kind !== 'face') return planeToWorldMatrix(planeBasis(plane));
    const input = ctx.inputs.get(plane.face.$feature);
    if (!input || input.kind !== 'body' || !input.capPlanes) {
      throw new Error('Sketch face reference does not resolve to a planar face');
    }
    if (plane.face.path === 'face:top') return input.capPlanes.top;
    if (plane.face.path === 'face:bottom') return input.capPlanes.bottom;
    throw new Error(`Unknown face "${plane.face.path}" — expected face:top or face:bottom`);
  }

  private async extrude(
    feature: FeatureNode<'extrude'>,
    ctx: EvalContext,
  ): Promise<GeometryResult> {
    const params = feature.params as ExtrudeParams;
    const regions = this.resolveProfiles(feature, ctx, params.profile);

    const back = params.distanceBack ?? 0;
    const wBottom = -back;
    const wTop = params.distance;
    if (Math.abs(wTop - wBottom) < 1e-9) {
      throw new FeatureError(feature.id, 'Extrude distance cannot be zero');
    }
    const endScale = Math.max(0, Math.min(100, params.endScale ?? 1));
    if (endScale < 1e-6 && regions.some((r) => r.holes.length > 0)) {
      throw new FeatureError(feature.id, 'A profile with holes cannot taper to a point');
    }
    // endScale always applies at the PUSHED end (w = distance); the sketch
    // side stays at 1. extrudeProfiles normalizes bottom/top ordering itself.
    const tool = extrudeProfiles(regions, wBottom, wTop, 1, endScale);
    const m = regions[0]!.planeToWorld;
    const capPlanes = {
      top: offsetPlaneMatrix(m, wTop),
      bottom: offsetPlaneMatrix(m, wBottom),
    };
    const result = await this.applyOp(feature, ctx, params.op, tool);
    return result.kind === 'body' ? { ...result, capPlanes } : result;
  }

  private async sweep(feature: FeatureNode<'sweep'>, ctx: EvalContext): Promise<GeometryResult> {
    const params = feature.params as SweepParams;
    const regions = this.resolveProfiles(feature, ctx, params.profile);
    const pathSketch = ctx.inputs.get(params.path.$feature);
    if (!pathSketch || pathSketch.kind !== 'sketch') {
      throw new FeatureError(feature.id, 'Sweep requires a path sketch');
    }
    const chain = pathSketch.paths[0];
    if (!chain || chain.length < 2) {
      throw new FeatureError(
        feature.id,
        'Path sketch has no open curve — draw an unclosed line/arc chain',
      );
    }
    const worldPath = chain.map(
      (p) => uvToWorld(pathSketch.planeWorld, p) as [number, number, number],
    );
    try {
      const tool = sweepProfiles(regions, worldPath);
      return this.applyOp(feature, ctx, params.op, tool);
    } catch (err) {
      throw new FeatureError(feature.id, err instanceof Error ? err.message : String(err));
    }
  }

  private async revolve(
    feature: FeatureNode<'revolve'>,
    ctx: EvalContext,
  ): Promise<GeometryResult> {
    const params = feature.params as RevolveParams;
    const regions = this.resolveProfiles(feature, ctx, params.profile);
    let tool;
    try {
      tool = revolveProfiles(regions, params.axisOrigin, params.axisDirection, params.angle);
    } catch (err) {
      throw new FeatureError(feature.id, err instanceof Error ? err.message : String(err));
    }
    return this.applyOp(feature, ctx, params.op, tool);
  }

  /** Resolve a profile ref ("profile:N" or whole sketch) to regions. */
  private resolveProfiles(
    feature: FeatureNode,
    ctx: EvalContext,
    profile: { readonly $feature: FeatureId; readonly path?: string },
  ): readonly ProfileRegion[] {
    const input = ctx.inputs.get(profile.$feature);
    if (!input || input.kind !== 'sketch') {
      throw new FeatureError(feature.id, `${feature.type} requires a sketch profile`);
    }
    if (input.profiles.length === 0) {
      throw new FeatureError(feature.id, 'Sketch has no closed profile');
    }
    if (profile.path !== undefined) {
      const match = /^profile:(\d+)$/.exec(profile.path);
      const region = input.profiles[match ? Number(match[1]) : NaN];
      if (!region) {
        throw new FeatureError(feature.id, `Profile "${profile.path}" does not exist`);
      }
      return [region];
    }
    return input.profiles;
  }

  /** new-body passes through; join/cut/intersect run against the world set. */
  private async applyOp(
    feature: FeatureNode,
    ctx: EvalContext,
    op: 'new-body' | 'join' | 'cut' | 'intersect',
    tool: MeshData,
  ): Promise<GeometryResult> {
    if (op === 'new-body') return { kind: 'body', mesh: tool };
    const targets = [...ctx.worldBodies.values()].map((v) => v.mesh);
    if (targets.length === 0) {
      throw new FeatureError(feature.id, `Nothing to ${op} — no earlier bodies exist`);
    }
    try {
      const mesh = await applyBoolean(op, targets, tool);
      return { kind: 'body', mesh, consumed: [...ctx.worldBodies.keys()] };
    } catch (err) {
      throw new FeatureError(feature.id, err instanceof Error ? err.message : String(err));
    }
  }

  /** Axis-aligned (possibly non-uniform) scale; consumes the target body. */
  private scale(feature: FeatureNode<'scale'>, ctx: EvalContext): GeometryResult {
    const params = feature.params as ScaleParams;
    const uniform = params.factor ?? 1;
    const [fx, fy, fz] = params.factors ?? [uniform, uniform, uniform];
    for (const f of [fx, fy, fz]) {
      if (!(f > 1e-4) || !Number.isFinite(f)) {
        throw new FeatureError(feature.id, 'Scale factors must be positive numbers');
      }
    }
    const input = ctx.inputs.get(params.target.$feature);
    if (!input || input.kind !== 'body') {
      throw new FeatureError(feature.id, 'Scale requires a body to scale');
    }
    const [cx, cy, cz] = params.center;
    const scalePoints = (src: Float32Array): Float32Array => {
      const out = new Float32Array(src.length);
      for (let i = 0; i < src.length; i += 3) {
        out[i] = cx + (src[i]! - cx) * fx;
        out[i + 1] = cy + (src[i + 1]! - cy) * fy;
        out[i + 2] = cz + (src[i + 2]! - cz) * fz;
      }
      return out;
    };
    // Normals transform by the inverse-transpose: n'ₖ = nₖ / fₖ, renormalized.
    const normals = new Float32Array(input.mesh.normals.length);
    for (let i = 0; i < normals.length; i += 3) {
      const nx = input.mesh.normals[i]! / fx;
      const ny = input.mesh.normals[i + 1]! / fy;
      const nz = input.mesh.normals[i + 2]! / fz;
      const len = Math.hypot(nx, ny, nz) || 1;
      normals[i] = nx / len;
      normals[i + 1] = ny / len;
      normals[i + 2] = nz / len;
    }
    const mesh = {
      positions: scalePoints(input.mesh.positions),
      normals,
      indices: input.mesh.indices.slice(),
      edgePositions: scalePoints(input.mesh.edgePositions),
    };
    const result: GeometryResult = {
      kind: 'body',
      mesh,
      consumed: [params.target.$feature],
    };
    // Cap planes stay valid only under uniform scale (otherwise the plane's
    // in-plane axes would shear) — keep sketch-on-face for that case only.
    const isUniform = Math.abs(fx - fy) < 1e-9 && Math.abs(fy - fz) < 1e-9;
    if (input.capPlanes && isUniform) {
      const scaleOrigin = (m: Float32Array): Float32Array => {
        const out = new Float32Array(m);
        out[12] = cx + (m[12]! - cx) * fx;
        out[13] = cy + (m[13]! - cy) * fy;
        out[14] = cz + (m[14]! - cz) * fz;
        return out;
      };
      return {
        ...result,
        capPlanes: {
          top: scaleOrigin(input.capPlanes.top),
          bottom: scaleOrigin(input.capPlanes.bottom),
        },
      };
    }
    return result;
  }

  /**
   * Move/rotate with optional copy array. Instance i (i = 0..copies) gets
   * rotation i·angle about the pivot followed by translation i·t — pure
   * moves array linearly, pure rotations array circularly, both combined
   * give a helix-style pattern. copies = 0 replaces the body (one instance
   * at i = 1); copies ≥ 1 keeps the original (i = 0) plus the copies.
   */
  private transform(feature: FeatureNode<'transform'>, ctx: EvalContext): GeometryResult {
    const params = feature.params as TransformParams;
    const input = ctx.inputs.get(params.target.$feature);
    if (!input || input.kind !== 'body') {
      throw new FeatureError(feature.id, 'Move/Rotate requires a body');
    }
    const copies = Math.max(0, Math.min(200, Math.round(params.copies)));
    const steps = copies === 0 ? [1] : Array.from({ length: copies + 1 }, (_v, i) => i);
    const mesh = this.buildTransformArray(feature, params, input.mesh, steps);

    const result: GeometryResult = {
      kind: 'body',
      mesh,
      consumed: [params.target.$feature],
    };
    // Pure single move keeps sketchable caps (translate them along).
    if (input.capPlanes && copies === 0 && Math.abs(params.rotationAngle) < 1e-12) {
      const shift = (m: Float32Array): Float32Array => {
        const out = new Float32Array(m);
        out[12] = m[12]! + params.translation[0];
        out[13] = m[13]! + params.translation[1];
        out[14] = m[14]! + params.translation[2];
        return out;
      };
      return {
        ...result,
        capPlanes: {
          top: shift(input.capPlanes.top),
          bottom: shift(input.capPlanes.bottom),
        },
      };
    }
    return result;
  }

  /**
   * Component instance: transformed copy/copies of a source body WITHOUT
   * consuming it — the source keeps displaying and every instance tracks it
   * through regeneration (that's the component behavior).
   */
  private instance(feature: FeatureNode<'instance'>, ctx: EvalContext): GeometryResult {
    const params = feature.params as InstanceParams;
    const input = ctx.inputs.get(params.source.$feature);
    if (!input || input.kind !== 'body') {
      throw new FeatureError(feature.id, 'Instance requires a source body');
    }
    const copies = Math.max(0, Math.min(200, Math.round(params.copies)));
    // Placements at 1..copies+1 — step 0 is the source itself, still shown.
    const steps = Array.from({ length: copies + 1 }, (_v, i) => i + 1);
    const mesh = this.buildTransformArray(feature, params, input.mesh, steps);
    return { kind: 'body', mesh };
  }

  /** Shared rotate-about-pivot + translate array builder. */
  private buildTransformArray(
    feature: FeatureNode,
    params: {
      readonly translation: readonly [number, number, number];
      readonly rotationAxis: 'X' | 'Y' | 'Z';
      readonly rotationAngle: number;
      readonly rotationCenter: readonly [number, number, number];
    },
    src: MeshData,
    instances: readonly number[],
  ): MeshData {
    if (![params.rotationAngle, ...params.translation].every(Number.isFinite)) {
      throw new FeatureError(feature.id, 'Transform parameters must be finite numbers');
    }
    const axisIdx = params.rotationAxis === 'X' ? 0 : params.rotationAxis === 'Y' ? 1 : 2;
    const [cx, cy, cz] = params.rotationCenter;
    const rotOf = (angle: number): number[] => {
      // 3x3 row-major rotation about the world axis.
      const c = Math.cos(angle);
      const s = Math.sin(angle);
      if (axisIdx === 0) return [1, 0, 0, 0, c, -s, 0, s, c];
      if (axisIdx === 1) return [c, 0, s, 0, 1, 0, -s, 0, c];
      return [c, -s, 0, s, c, 0, 0, 0, 1];
    };

    const vertCount = src.positions.length;
    const positions = new Float32Array(vertCount * instances.length);
    const normals = new Float32Array(vertCount * instances.length);
    const indices = new Uint32Array(src.indices.length * instances.length);
    const edgePositions = new Float32Array(src.edgePositions.length * instances.length);

    instances.forEach((step, inst) => {
      const R = rotOf(params.rotationAngle * step);
      const [tx, ty, tz] = params.translation.map((v) => v * step);
      const mapPoint = (
        out: Float32Array,
        srcArr: Float32Array,
        outBase: number,
      ): void => {
        for (let i = 0; i < srcArr.length; i += 3) {
          const x = srcArr[i]! - cx;
          const y = srcArr[i + 1]! - cy;
          const z = srcArr[i + 2]! - cz;
          out[outBase + i] = cx + R[0]! * x + R[1]! * y + R[2]! * z + tx!;
          out[outBase + i + 1] = cy + R[3]! * x + R[4]! * y + R[5]! * z + ty!;
          out[outBase + i + 2] = cz + R[6]! * x + R[7]! * y + R[8]! * z + tz!;
        }
      };
      mapPoint(positions, src.positions, inst * vertCount);
      mapPoint(edgePositions, src.edgePositions, inst * src.edgePositions.length);
      for (let i = 0; i < src.normals.length; i += 3) {
        const x = src.normals[i]!;
        const y = src.normals[i + 1]!;
        const z = src.normals[i + 2]!;
        const o = inst * vertCount + i;
        normals[o] = R[0]! * x + R[1]! * y + R[2]! * z;
        normals[o + 1] = R[3]! * x + R[4]! * y + R[5]! * z;
        normals[o + 2] = R[6]! * x + R[7]! * y + R[8]! * z;
      }
      const idxBase = inst * src.indices.length;
      const vertBase = (inst * vertCount) / 3;
      for (let i = 0; i < src.indices.length; i++) {
        indices[idxBase + i] = src.indices[i]! + vertBase;
      }
    });

    return { positions, normals, indices, edgePositions };
  }

  /** Group: concatenate several bodies into one (consumes them all). */
  private group(feature: FeatureNode<'group'>, ctx: EvalContext): GeometryResult {
    const params = feature.params as GroupParams;
    if (params.targets.length < 2) {
      throw new FeatureError(feature.id, 'A group needs at least two bodies');
    }
    const meshes: MeshData[] = [];
    for (const t of params.targets) {
      const input = ctx.inputs.get(t.$feature);
      if (!input || input.kind !== 'body') {
        throw new FeatureError(feature.id, 'Group members must be bodies');
      }
      meshes.push(input.mesh);
    }
    const total = meshes.reduce(
      (acc, m) => ({
        pos: acc.pos + m.positions.length,
        idx: acc.idx + m.indices.length,
        edge: acc.edge + m.edgePositions.length,
      }),
      { pos: 0, idx: 0, edge: 0 },
    );
    const positions = new Float32Array(total.pos);
    const normals = new Float32Array(total.pos);
    const indices = new Uint32Array(total.idx);
    const edgePositions = new Float32Array(total.edge);
    let pOff = 0, iOff = 0, eOff = 0;
    for (const m of meshes) {
      positions.set(m.positions, pOff);
      normals.set(m.normals, pOff);
      for (let i = 0; i < m.indices.length; i++) indices[iOff + i] = m.indices[i]! + pOff / 3;
      edgePositions.set(m.edgePositions, eOff);
      pOff += m.positions.length;
      iOff += m.indices.length;
      eOff += m.edgePositions.length;
    }
    return {
      kind: 'body',
      mesh: { positions, normals, indices, edgePositions },
      consumed: params.targets.map((t) => t.$feature),
    };
  }

  /**
   * Assembly component (mesh fallback): merge the source project's bodies
   * and place them — Euler XYZ rotation about the origin, then translate.
   */
  private part(feature: FeatureNode<'part'>, ctx: EvalContext): GeometryResult {
    const params = feature.params as unknown as {
      position: readonly [number, number, number];
      rotationEuler: readonly [number, number, number];
      targets: readonly { $feature: FeatureId }[];
    };
    const meshes: MeshData[] = [];
    for (const t of params.targets ?? []) {
      const input = ctx.inputs.get(t.$feature);
      if (input?.kind === 'body') meshes.push(input.mesh);
    }
    if (meshes.length === 0) throw new FeatureError(feature.id, 'Part has no source bodies');

    // Combined rotation R = Rz·Ry·Rx (row-major 3x3).
    const [rx, ry, rz] = params.rotationEuler;
    const [cX, sX] = [Math.cos(rx), Math.sin(rx)];
    const [cY, sY] = [Math.cos(ry), Math.sin(ry)];
    const [cZ, sZ] = [Math.cos(rz), Math.sin(rz)];
    const R = [
      cZ * cY, cZ * sY * sX - sZ * cX, cZ * sY * cX + sZ * sX,
      sZ * cY, sZ * sY * sX + cZ * cX, sZ * sY * cX - cZ * sX,
      -sY, cY * sX, cY * cX,
    ];
    const [px, py, pz] = params.position;
    const mapPoints = (src: Float32Array, out: Float32Array, off: number, translate: boolean): void => {
      for (let i = 0; i < src.length; i += 3) {
        const x = src[i]!, y = src[i + 1]!, z = src[i + 2]!;
        out[off + i] = R[0]! * x + R[1]! * y + R[2]! * z + (translate ? px : 0);
        out[off + i + 1] = R[3]! * x + R[4]! * y + R[5]! * z + (translate ? py : 0);
        out[off + i + 2] = R[6]! * x + R[7]! * y + R[8]! * z + (translate ? pz : 0);
      }
    };

    const total = meshes.reduce(
      (acc, m) => ({
        pos: acc.pos + m.positions.length,
        idx: acc.idx + m.indices.length,
        edge: acc.edge + m.edgePositions.length,
      }),
      { pos: 0, idx: 0, edge: 0 },
    );
    const positions = new Float32Array(total.pos);
    const normals = new Float32Array(total.pos);
    const indices = new Uint32Array(total.idx);
    const edgePositions = new Float32Array(total.edge);
    let pOff = 0, iOff = 0, eOff = 0;
    for (const m of meshes) {
      mapPoints(m.positions, positions, pOff, true);
      mapPoints(m.normals, normals, pOff, false);
      for (let i = 0; i < m.indices.length; i++) indices[iOff + i] = m.indices[i]! + pOff / 3;
      mapPoints(m.edgePositions, edgePositions, eOff, true);
      pOff += m.positions.length;
      iOff += m.indices.length;
      eOff += m.edgePositions.length;
    }
    return { kind: 'body', mesh: { positions, normals, indices, edgePositions } };
  }

  /**
   * Mirror across a world-aligned plane. Reflection flips orientation, so
   * the mirrored copy's triangle winding is reversed to stay outward-facing.
   */
  private mirror(feature: FeatureNode<'mirror'>, ctx: EvalContext): GeometryResult {
    const params = feature.params as MirrorParams;
    const input = ctx.inputs.get(params.target.$feature);
    if (!input || input.kind !== 'body') {
      throw new FeatureError(feature.id, 'Mirror requires a body');
    }
    const axis = params.plane === 'YZ' ? 0 : params.plane === 'XZ' ? 1 : 2;
    const src = input.mesh;

    const mirrorPoints = (arr: Float32Array): Float32Array => {
      const out = new Float32Array(arr.length);
      for (let i = 0; i < arr.length; i += 3) {
        out[i] = arr[i]!;
        out[i + 1] = arr[i + 1]!;
        out[i + 2] = arr[i + 2]!;
        out[i + axis] = 2 * params.offset - arr[i + axis]!;
      }
      return out;
    };
    const mPositions = mirrorPoints(src.positions);
    const mNormals = new Float32Array(src.normals.length);
    for (let i = 0; i < mNormals.length; i += 3) {
      mNormals[i] = src.normals[i]!;
      mNormals[i + 1] = src.normals[i + 1]!;
      mNormals[i + 2] = src.normals[i + 2]!;
      mNormals[i + axis] = -src.normals[i + axis]!;
    }
    const mIndices = new Uint32Array(src.indices.length);
    for (let t = 0; t < src.indices.length; t += 3) {
      mIndices[t] = src.indices[t]!;
      mIndices[t + 1] = src.indices[t + 2]!; // reversed winding
      mIndices[t + 2] = src.indices[t + 1]!;
    }
    const mEdges = mirrorPoints(src.edgePositions);

    let mesh;
    if (params.keepOriginal) {
      const vertCount = src.positions.length / 3;
      const positions = new Float32Array(src.positions.length * 2);
      positions.set(src.positions);
      positions.set(mPositions, src.positions.length);
      const normals = new Float32Array(src.normals.length * 2);
      normals.set(src.normals);
      normals.set(mNormals, src.normals.length);
      const indices = new Uint32Array(src.indices.length * 2);
      indices.set(src.indices);
      for (let i = 0; i < mIndices.length; i++) {
        indices[src.indices.length + i] = mIndices[i]! + vertCount;
      }
      const edgePositions = new Float32Array(src.edgePositions.length * 2);
      edgePositions.set(src.edgePositions);
      edgePositions.set(mEdges, src.edgePositions.length);
      mesh = { positions, normals, indices, edgePositions };
    } else {
      mesh = { positions: mPositions, normals: mNormals, indices: mIndices, edgePositions: mEdges };
    }
    // Cap planes don't survive reflection cleanly (in-plane axes flip) — drop.
    return { kind: 'body', mesh, consumed: [params.target.$feature] };
  }

  /** Non-parametric reference mesh: load from the asset store, bake transform. */
  private async importMesh(feature: FeatureNode<'import-mesh'>): Promise<GeometryResult> {
    const params = feature.params as ImportMeshParams;
    const mesh = await loadParsedMesh(params.assetHash);
    if (!mesh) {
      throw new FeatureError(
        feature.id,
        `Asset "${params.name}" is missing from local storage — re-import the file`,
      );
    }
    return { kind: 'body', mesh: bakeTransform(mesh, params.transform) };
  }
}
